import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import Pagination from "@shared/v2/Pagination";
import TabBar from "@shared/v2/TabBar";
import Tab from "@shared/v2/Tab";
import TextButton from "@shared/v2/TextButton";
import { Section } from "../BusinessDashboard";
import { Table, TBody, TD, TH, THead, TR } from "../BusinessDashboard/Table/Table";
import useOpenHouseDashboardList from "./hooks/useOpenHouseDashboardList";
import OpenHouseFlyout from "./OpenHouseFlyout";
import Loading from "../../../Goals/components/Loader";
import openLeadIndexFromTagName from "./helpers/openLeadIndexFromTagName";
import SignInSheetDropdown from "./SignInSheetDropdown";
import useUpcomingCount from "./hooks/useUpcomingCount";

const OpenHouseDashboardList = ({ dateRange, selectedHosts, onEdit, formOptions }) => {
  const [status, setStatus] = useState("completed");
  const [openHouse, setOpenHouse] = useState(null);
  const { actions, data, loading } = useOpenHouseDashboardList({ dateRange, selectedHosts, status });
  const upcomingCount = useUpcomingCount({ dateRange, selectedHosts });

  useEffect(() => {
    if (!data.list?.find((oh) => oh.uuid === openHouse?.uuid)) {
      setOpenHouse(null);
    } else {
      setOpenHouse({ uuid: openHouse.uuid });
    }
  }, [data.list]);

  return (
    <Section className="tw-gap-[16px]">
      <div className="tw-mt-[-12px]">
        <TabBar>
          <Tab label="Completed" selected={status === "completed"} onClick={() => setStatus("completed")} />
          <Tab
            label={
              <span className="tw-flex tw-items-center tw-gap-[4px]">
                Upcoming
                {upcomingCount > 0 && (
                  <div className="tw-flex tw-justify-center tw-items-center tw-px-[4px] tw-min-w-[20px] tw-h-[20px] tw-bg-theme-primary tw-text-12d tw-text-white tw-rounded-full">
                    {upcomingCount}
                  </div>
                )}
              </span>
            }
            selected={status === "upcoming"}
            onClick={() => setStatus("upcoming")}
          />
        </TabBar>
      </div>
      <Table>
        <THead>
          <TR className="tw-border-x-0">
            <TH className="tw-border-l-0 tw-py-[8px] tw-font-semibold">Address</TH>
            <TH className="tw-py-[8px] tw-font-semibold">Date & Time</TH>
            <TH className="tw-py-[8px] tw-font-semibold">Host</TH>
            <TH className="tw-py-[8px] tw-font-semibold tw-text-center">Attendees</TH>
            <TH className="tw-py-[8px] tw-font-semibold tw-text-center">Leads</TH>
            {status === "upcoming" && <TH />}
          </TR>
        </THead>
        <TBody>
          {data.list?.map(
            (
              {
                address,
                attendees,
                hostName,
                leads,
                startDate,
                startTime,
                endTime,
                uuid,
                activeAt,
                completedAt,
              },
              i,
            ) => (
              <TR
                key={uuid}
                className="tw-border-x-0 hover:tw-bg-[#fafafa] active:tw-bg-gray-5 tw-transition-colors tw-ease-out tw-cursor-pointer"
                onClick={() => setOpenHouse({ uuid })}
              >
                <TD>
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-font-semibold">{address.streetAddress}</span>
                    <span className="tw-text-gray-50">
                      {address.city}, {address.state} {address.postalCode}
                    </span>
                  </div>
                </TD>
                <TD>
                  <div className="tw-flex tw-flex-col">
                    <span>{dayjs(startDate, "YYYY-MM-DD").format("ddd. MM/DD/YYYY")}</span>
                    <span className="tw-text-gray-50">
                      {activeAt && !completedAt ? (
                        <span className="tw-text-semantic-yellow-100">In Progress</span>
                      ) : (
                        `${dayjs(startTime, "HH:mm:ss").format("hh:mm A")} - ${dayjs(endTime, "HH:mm:ss").format("hh:mm A")}`
                      )}
                    </span>
                  </div>
                </TD>
                <TD>{hostName}</TD>
                <TD className="tw-text-center">{attendees}</TD>
                <TD className="tw-text-center">
                  {leads > 0 ? (
                    <TextButton
                      className="!tw-font-normal"
                      size="medium"
                      onClick={openLeadIndexFromTagName(address.streetAddress)}
                    >
                      {leads}
                    </TextButton>
                  ) : (
                    leads
                  )}
                </TD>
                <TD className="tw-text-right">
                  {!completedAt && (
                    <SignInSheetDropdown
                      className="tw-justify-end"
                      selectedHosts={selectedHosts}
                      openHouse={data.list[i]}
                    />
                  )}
                </TD>
              </TR>
            ),
          )}
        </TBody>
      </Table>

      {data.totalPages > 1 && (
        <div className="tw-flex tw-justify-center">
          <Pagination
            currentPage={data.page}
            totalPages={data.totalPages}
            onGoToPage={(p) => actions.setPage(p)}
          />
        </div>
      )}

      {loading && <Loading />}

      <OpenHouseFlyout
        openHouse={openHouse}
        onClose={() => setOpenHouse(null)}
        onDelete={actions.delete}
        onEdit={onEdit}
        selectedHosts={selectedHosts}
        formOptions={formOptions}
      />
    </Section>
  );
};

OpenHouseDashboardList.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  selectedHosts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onEdit: PropTypes.func.isRequired,
  formOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default OpenHouseDashboardList;
